/**
 * 사용 안내 창 — 주제별 **요약 한 줄과 요점 몇 줄만.** 자세한 것은 주제마다 달린
 * 「AI 에이전트에 물어보기」로 에이전트에게 넘긴다. 사용 설명서는 쓰는 순간부터 낡고
 * 읽히지도 않는다 — 여기는 훑는 화면이다.
 *
 * 안내 데이터는 서버(`help/usageGuide.js`)가 준다. 에이전트 도구 `get_usage_guide` 가 같은 것을
 * 읽으므로 화면과 에이전트가 다른 말을 하지 않는다. 그래서 이 문구는 백엔드 정책대로 한국어뿐이다.
 *
 * 「물어보기」는 채팅창을 열고 질문을 입력란에 **채우기만** 한다 — 사용자가 고쳐 보낼 수 있게.
 */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FiMessageSquare, FiChevronDown, FiChevronRight, FiX } from 'react-icons/fi';
import { getUsageGuide } from '../services/assistantClient';
import './HelpGuideModal.css';

function HelpGuideModal({ open, onClose, onAsk }) {
    const { t } = useTranslation();
    const [topics, setTopics] = useState([]);
    const [error, setError] = useState(null);
    const [openId, setOpenId] = useState(null);

    useEffect(() => {
        if (!open) return;
        setError(null);
        getUsageGuide().then(r => setTopics(r.topics || [])).catch(err => setError(err.message));
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!open) return null;

    return (
        <div className="hg-overlay" onClick={onClose}>
            <div className="hg-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                <div className="hg-head">
                    <h4>{t('menu:help.guide')}</h4>
                    {/* AI 에게 묻는 버튼은 에이전트가 있을 때만 — AI 제외 빌드는 onAsk 를 넘기지 않는다 */}
                    {onAsk && (
                        <button type="button" className="hg-ask hg-ask-top" onClick={() => onAsk(t('assistant:help.btn.quickStart'))}>
                            <FiMessageSquare size={12} /> {t('menu:help.quickStart')}
                        </button>
                    )}
                    <button type="button" className="hg-close" onClick={onClose} title={t('common:ui.close')}><FiX size={15} /></button>
                </div>
                <p className="hg-intro">{t('menu:help.guideIntro')}</p>

                {error ? <div className="hg-error">{error}</div> : null}

                <div className="hg-list">
                    {topics.map((tp) => {
                        const isOpen = openId === tp.id;
                        return (
                            <div key={tp.id} className={`hg-topic ${isOpen ? 'hg-topic-open' : ''}`}>
                                <button type="button" className="hg-topic-head" onClick={() => setOpenId(isOpen ? null : tp.id)}>
                                    {isOpen ? <FiChevronDown size={12} /> : <FiChevronRight size={12} />}
                                    <span className="hg-topic-title">{tp.title}</span>
                                    <span className="hg-topic-summary">{tp.summary}</span>
                                </button>
                                {isOpen && (
                                    <div className="hg-topic-body">
                                        <ol>
                                            {tp.steps.map((s, i) => <li key={i}>{s}</li>)}
                                        </ol>
                                        {onAsk && (
                                            <button
                                                type="button" className="hg-ask"
                                                onClick={() => onAsk(t('assistant:help.askTopic', { title: tp.title }))}
                                            >
                                                <FiMessageSquare size={12} /> {t('menu:help.askAgent')}
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

HelpGuideModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    /** 없으면(AI 제외 빌드) 「AI 에게 묻기」 버튼을 그리지 않는다 */
    onAsk: PropTypes.func,
};

export default HelpGuideModal;
